import { Check, Sparkles } from 'lucide-react'
import * as Icons from 'lucide-react'
import { type ComponentType } from 'react'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

import { PreviewFrame } from './preview'
import { renderEmailDocument } from './renderer/render'
import { DEFAULT_STARTERS } from './templates'
import type { EmailStarterTemplate, EmailVariable } from './types'
import { buildMockVariableValues } from './preview'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    templates?: EmailStarterTemplate[]
    onSelect: (template: EmailStarterTemplate) => void
    /** Variables from the current template type — used to populate the thumbnail preview */
    variables?: EmailVariable[]
    title?: string
    description?: string
}

/**
 * Full-screen gallery that lets merchants pick a starter template.
 * Each card renders a faithful iframe thumbnail so merchants see exactly
 * what the email will look like, at full card width.
 */
export function StarterGallery(props: Props) {
    const {
        open,
        onOpenChange,
        templates = DEFAULT_STARTERS,
        onSelect,
        variables,
        title = 'Start from a template',
        description = 'Pick a polished starting point. You can edit everything after.',
    } = props

    const mockValues = buildMockVariableValues(variables)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl p-0 gap-0 overflow-hidden">
                <DialogHeader className="px-8 pb-5 pt-7">
                    <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="text-xl">{title}</DialogTitle>
                            <DialogDescription className="mt-0.5">
                                {description}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="max-h-[78vh] overflow-y-auto border-t bg-muted/30 px-8 py-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {templates.map((tpl) => (
                            <StarterCard
                                key={tpl.id}
                                template={tpl}
                                mockValues={mockValues}
                                onSelect={() => {
                                    onSelect(tpl)
                                    onOpenChange(false)
                                }}
                            />
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

function StarterCard(props: {
    template: EmailStarterTemplate
    mockValues: Record<string, string>
    onSelect: () => void
}) {
    const { template, mockValues, onSelect } = props
    const IconCmp = (
        Icons as unknown as Record<
            string,
            ComponentType<{ className?: string }>
        >
    )[template.icon] as ComponentType<{ className?: string }> | undefined
    const accent = template.accent || '#6366f1'
    const html = renderEmailDocument(template.doc, {
        variableValues: mockValues,
    })

    return (
        <button
            type="button"
            onClick={onSelect}
            className={cn(
                'group relative flex flex-col overflow-hidden rounded-2xl border bg-card text-left shadow-sm',
                'transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-xl',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            )}
        >
            {/* Header — concise title + accent icon, sits ABOVE the preview so the
                preview itself gets maximum space */}
            <div className="flex items-center gap-3 border-b bg-card px-5 py-4">
                <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${accent}18`, color: accent }}
                >
                    {IconCmp ? (
                        <IconCmp className="h-4.5 w-4.5" />
                    ) : (
                        <Icons.Mail className="h-4.5 w-4.5" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                        {template.label}
                    </div>
                    <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                        {template.description}
                    </div>
                </div>
                <span
                    className={cn(
                        'hidden shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors sm:inline-flex',
                        'border-border text-muted-foreground',
                        'group-hover:border-primary/50 group-hover:bg-primary group-hover:text-primary-foreground',
                    )}
                >
                    <Check className="h-3 w-3" />
                    Use
                </span>
            </div>

            {/* Preview — fit-width so it's pixel-perfect at card width, clamped
                height so we show the top slice without pushing the footer down */}
            <div className="relative w-full overflow-hidden bg-muted/40">
                <PreviewFrame
                    html={html}
                    subject={template.defaultSubject}
                    preheader={template.defaultPreheader}
                    fromName="Your store"
                    fromEmail="noreply@yourstore.com"
                    scale="fit-width"
                    maxHeight={420}
                    pageBg="#f9fafb"
                    className="w-full"
                    title={`${template.label} preview`}
                />
                {/* Bottom fade hinting there is more content below the fold */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-card/95 via-card/40 to-transparent"
                />
            </div>
        </button>
    )
}
